import { useCallback } from 'react';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { useClaimAttention } from '../../../store';

export type ClaimDispositionSource =
  | 'claims_list'
  | 'work_queue'
  | 'overview'
  | 'claims_review'
  | 'encounter_worklist'
  | 'encounter_claim';

interface UseClaimDispositionOptions {
  /** Where the disposition was triggered — flows through to Statsig. */
  source: ClaimDispositionSource;
  /** Called after a successful dismiss/snooze (e.g. advance selection). */
  onDispositioned?: () => void;
  attentionLane?: string;
  signals?: string[];
}

/** Pull an `error_code` out of an RTK Query error payload, if present. */
function extractErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const data = (error as Record<string, unknown>).data;
  if (data && typeof data === 'object') {
    return (data as { error_code?: string }).error_code;
  }
  return undefined;
}

/**
 * Shared dismiss / "remind me tomorrow" behavior for a claim, with Statsig
 * tracking and failure snackbars baked in. Backs every claim-disposition
 * surface: the claims review queue, the encounter worklist row menu, and the
 * encounter-detail claim sub-tab.
 */
export function useClaimDisposition({
  source,
  onDispositioned,
  attentionLane,
  signals,
}: UseClaimDispositionOptions) {
  const { enqueueSnackbar } = useSnackbar();
  const { billing } = useEventTracking();
  const { dismiss: dismissClaim, remindTomorrow: snoozeClaim, isUpdating } = useClaimAttention();

  const dismiss = useCallback(
    async (claimId: string) => {
      const result = await dismissClaim(claimId);
      if (result.success) {
        billing.claimAttentionDismissed(claimId, {
          source,
          attention_lane: attentionLane,
          signals,
        });
        onDispositioned?.();
      } else {
        billing.claimAttentionFailed(claimId, {
          action: 'dismiss',
          source,
          error_code: extractErrorCode(result.error),
        });
        enqueueSnackbar('Failed to dismiss', { variant: 'error' });
      }
    },
    [dismissClaim, billing, source, attentionLane, signals, onDispositioned, enqueueSnackbar],
  );

  const remindTomorrow = useCallback(
    async (claimId: string) => {
      const result = await snoozeClaim(claimId);
      if (result.success) {
        billing.claimAttentionSnoozed(claimId, {
          source,
          attention_lane: attentionLane,
          signals,
        });
        onDispositioned?.();
      } else {
        billing.claimAttentionFailed(claimId, {
          action: 'snooze',
          source,
          error_code: extractErrorCode(result.error),
        });
        enqueueSnackbar('Failed to snooze', { variant: 'error' });
      }
    },
    [snoozeClaim, billing, source, attentionLane, signals, onDispositioned, enqueueSnackbar],
  );

  return { dismiss, remindTomorrow, isUpdating };
}
